import React, { useEffect, useState } from "react";
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { CRYPTO_WALLETS, FIAT_WALLETS } from "../../../constants/wallets";
import { cardService } from "../../../services/api/card.service";
import { selectNetwork, selectTokenType, selectSymbol } from "../../../lib/utils";
import { transferService } from "../../../services/api/transfer.service";

import { MainWallet } from "../../../types/rail.types";
import { useSelector, useDispatch } from 'react-redux';
import type { AppDispatch, RootState } from '../../../store';
import { getMasterCardBalance, getZenusAccount } from '../../../store/assetsReducer';

interface WalletInfo {
  address: string;
  balance: string;
  currency: string;
  displayName: string;
  isToken: boolean;
  network: string;
}

interface Wallets {
  fiat: MainWallet[];
  crypto: WalletInfo[];
}

interface TopUpModalProps {
  isOpen: boolean;
  onClose: (value: boolean) => void;
  cardId: string;
}

export default function TopUpModal({ isOpen, onClose, cardId }: TopUpModalProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { data: { mainWallets, cryptoWallets, masterCard, zenusAccount } } = useSelector((state: RootState) => state.assets);
  const { data: { fiatPrices } } = useSelector((state: RootState) => state.setting);
  const [wallet, setWallet] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [isPending, setIsPending] = useState<boolean>(false);
  const [option, setOption] = useState<string>("main");
  const [wallets, setWallets] = useState<Wallets>({
    fiat: [],
    crypto: [],
  });
  const [reloadCosts, setReloadCosts] = useState<any>(null);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const [zenusTopupCosts, setZenusTopupCosts] = useState<any>(null);

  // Calcular costos de recarga Zenus en tiempo real
  const calculateZenusTopupCosts = async (eurAmount: string) => {
    if (!eurAmount || parseFloat(eurAmount) < 10 || parseFloat(eurAmount) > 5000) {
      setZenusTopupCosts(null);
      return;
    }

    setIsCalculating(true);
    try {
      console.log('🔄 Calculating Zenus topup costs for:', parseFloat(eurAmount), 'EUR');
      const costs = await cardService.calculateZenusTopupCosts(parseFloat(eurAmount));
      console.log('✅ Zenus topup costs received:', costs);
      setZenusTopupCosts(costs);
    } catch (error: any) {
      console.error('❌ Error calculating Zenus topup costs:', error);
      setZenusTopupCosts(null);
      toast.error(error.message || 'Error calculating costs');
    } finally {
      setIsCalculating(false);
    }
  };

  // Calcular costos de recarga crypto en tiempo real
  const calculateReloadCosts = async (eurAmount: string, selectedWallet: WalletInfo) => {
    if (!eurAmount || parseFloat(eurAmount) < 10 || parseFloat(eurAmount) > 10000 || !selectedWallet) {
      setReloadCosts(null);
      return;
    }

    setIsCalculating(true);
    try {
      const network = selectedWallet.network.toLowerCase();
      const token = selectedWallet.isToken
        ? selectedWallet.currency.includes('_')
          ? selectedWallet.currency.split('_')[0].toUpperCase()
          : undefined
        : undefined;

      const costs = await cardService.getCryptoReloadCosts(cardId, {
        network,
        token,
        amountEUR: parseFloat(eurAmount)
      });

      setReloadCosts(costs);
    } catch (error: any) {
      console.error('Error calculating reload costs:', error);
      setReloadCosts(null);
    } finally {
      setIsCalculating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (option === "crypto") {
      const selectedWallet = wallets.crypto.filter((item) => item.currency === wallet);
      if (selectedWallet.length === 0) {
        toast.error(t('cards.topUpModal.errors.selectWallet'));
        return;
      }

      const eurAmount = parseFloat(amount);
      if (eurAmount < 10) {
        toast.error(t('cards.topUpModal.errors.minAmount'));
        return;
      }
      if (eurAmount > 10000) {
        toast.error(t('cards.topUpModal.errors.maxAmount'));
        return;
      }

      // Verificar que se hayan calculado los costos
      if (!reloadCosts) {
        toast.error(t('cards.topUpModal.errors.calculateCosts'));
        return;
      }

      // Verificar balance suficiente del token/crypto
      const walletBalance = parseFloat(selectedWallet[0].balance);
      if (walletBalance < reloadCosts.totalCrypto) {
        toast.error(
          `Insufficient balance. Required: ${reloadCosts.totalCrypto.toFixed(6)} ${reloadCosts.cryptoSymbol}, Available: ${walletBalance.toFixed(6)} ${reloadCosts.cryptoSymbol}`
        );
        return;
      }

      // Si es un token, verificar que tenga gas suficiente en la crypto nativa
      if (selectedWallet[0].isToken) {
        const network = selectedWallet[0].network.toUpperCase();
        const walletAddress = selectedWallet[0].address;

        // Buscar la wallet nativa de la misma red y dirección
        let nativeWallet = null;
        let nativeSymbol = '';
        let minGasRequired = 0;

        if (network === 'ETHEREUM') {
          nativeSymbol = 'ETH';
          minGasRequired = 0.001; // ~$3 a precios actuales, suficiente para varias tx
          nativeWallet = wallets.crypto.find(w =>
            w.network === 'ETHEREUM' &&
            w.address === walletAddress &&
            w.currency === 'ETH'
          );
        } else if (network === 'BNB') {
          nativeSymbol = 'BNB';
          minGasRequired = 0.001; // ~$0.60, suficiente para varias tx
          nativeWallet = wallets.crypto.find(w =>
            w.network === 'BNB' &&
            w.address === walletAddress &&
            w.currency === 'BNB'
          );
        } else if (network === 'TRON') {
          nativeSymbol = 'TRX';
          minGasRequired = 15; // ~$3.75, suficiente para energy/bandwidth
          nativeWallet = wallets.crypto.find(w =>
            w.network === 'TRON' &&
            w.address === walletAddress &&
            w.currency === 'TRX'
          );
        } else if (network === 'SOLANA') {
          nativeSymbol = 'SOL';
          minGasRequired = 0.001; // ~$0.16, suficiente para varias tx
          nativeWallet = wallets.crypto.find(w =>
            w.network === 'SOLANA' &&
            w.address === walletAddress &&
            w.currency === 'SOL'
          );
        }

        // Verificar si tiene gas suficiente
        const nativeBalance = nativeWallet ? parseFloat(nativeWallet.balance) : 0;
        if (nativeBalance < minGasRequired) {
          toast.error(t('cards.topUpModal.errors.insufficientGas', {
            native: nativeSymbol,
            required: minGasRequired,
            available: nativeBalance.toFixed(6)
          }));
          return;
        }
      }

      setIsPending(true);

      try {
        const network = selectedWallet[0].network.toLowerCase();
        const token = selectedWallet[0].isToken
          ? selectedWallet[0].currency.includes('_')
            ? selectedWallet[0].currency.split('_')[0].toUpperCase()
            : undefined
          : undefined;

        const result = await cardService.createCryptoReload(cardId, {
          fromWallet: selectedWallet[0].address,
          network,
          token,
          amountEUR: eurAmount
        });

        // Actualizar balance automáticamente después de recarga
        if (masterCard.data[0]?.id) {
          const balanceData = {
            card: masterCard.data[0].id,
            fiatPrices
          };
          await dispatch(getMasterCardBalance(balanceData)).unwrap();
        }

        toast.success(t('cards.topUpModal.success'));
        onClose(false);
      } catch (error: any) {
        toast.error(error.message || t('cards.topUpModal.errors.processingFailed'));
        console.error(error);
      } finally {
        setIsPending(false);
      }
    } else if (option === "main") {
      // FIAT Wallet (Zenus USD)
      const selectedFiatWallet = wallets.fiat.find((w) => w.currency === wallet);
      if (!selectedFiatWallet) {
        toast.error(t('cards.topUpModal.errors.selectWallet'));
        return;
      }

      const eurAmount = parseFloat(amount);
      if (eurAmount < 10) {
        toast.error(t('cards.topUpModal.errors.minAmount'));
        return;
      }
      if (eurAmount > 5000) {
        toast.error('Maximum amount is €5000');
        return;
      }

      // Verificar que se hayan calculado los costos
      if (!zenusTopupCosts) {
        toast.error('Please wait for cost calculation');
        return;
      }

      // Verificar balance suficiente en USD
      const walletBalanceUSD = parseFloat(selectedFiatWallet.balance);
      if (walletBalanceUSD < zenusTopupCosts.totalUSD) {
        toast.error(
          `Insufficient balance. Required: $${zenusTopupCosts.totalUSD.toFixed(2)} USD, Available: $${walletBalanceUSD.toFixed(2)} USD`
        );
        return;
      }

      setIsPending(true);

      try {
        const result = await cardService.processZenusTopup({
          cardId,
          amountEUR: eurAmount,
          zenusAccountId: selectedFiatWallet.id
        });

        // Actualizar balances automáticamente después de recarga
        // 1. Actualizar balance de MasterCard
        if (masterCard.data[0]?.id) {
          const balanceData = {
            card: masterCard.data[0].id,
            fiatPrices
          };
          await dispatch(getMasterCardBalance(balanceData)).unwrap();
        }

        // 2. Actualizar balance de Zenus (FIAT Wallet)
        await dispatch(getZenusAccount()).unwrap();

        toast.success(t('cards.topUpModal.success'));
        onClose(false);
      } catch (error: any) {
        toast.error(error.message || t('cards.topUpModal.errors.processingFailed'));
        console.error(error);
      } finally {
        setIsPending(false);
      }
    }
  };

  const changeWallet = (currency: string) => {
    setWallet(currency);
  };

  const truncateDecimals = (num, maxDecimals) => {
    const [integer, decimal] = num.toString().split('.');
    if (!decimal) return integer;
    return `${integer}.${decimal.slice(0, maxDecimals)}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('🔍 TopUpModal - mainWallets:', mainWallets);
        console.log('🔍 TopUpModal - cryptoWallets:', cryptoWallets);
        console.log('🔍 TopUpModal - zenusAccount:', zenusAccount);

        // Procesar Fiat Wallets
        let allFiatWallets: MainWallet[] = [];

        if (mainWallets && mainWallets.fiatWallets && mainWallets.fiatWallets.data) {
          allFiatWallets = [...mainWallets.fiatWallets.data];
        }

        // Agregar Zenus USD wallet si existe
        if (zenusAccount && zenusAccount.account && zenusAccount.balance !== undefined) {
          const zenusWallet: MainWallet = {
            id: zenusAccount.account.id || 'zenus-usd',
            accountNumber: zenusAccount.account.accountNumber || 'N/A',
            currency: 'USD',
            balance: zenusAccount.balance.toString(),
            availableBalance: zenusAccount.balance.toString(),
            status: 'ACTIVE',
            type: 'ZENUS',
            name: 'Zenus USD',
            createdAt: zenusAccount.account.createdAt || new Date().toISOString(),
          } as MainWallet;

          allFiatWallets.push(zenusWallet);
        }

        console.log('💰 Fiat wallets (todas, incluyendo Zenus):', allFiatWallets);
        setWallets((prev) => ({
          ...prev,
          fiat: allFiatWallets,
        }));

        // Procesar Crypto Wallets con sus tokens
        if (cryptoWallets && cryptoWallets.data) {
          const processedWallets: WalletInfo[] = [];

          // Bitcoin
          (cryptoWallets.data.bitcoin || []).forEach((wallet: any) => {
            if (wallet.balances && wallet.balances.btc) {
              processedWallets.push({
                address: wallet.address,
                balance: wallet.balances.btc,
                currency: 'BTC',
                displayName: 'Bitcoin',
                isToken: false,
                network: 'BITCOIN'
              });
            }
          });

          // Ethereum (ETH nativa + tokens ERC20)
          (cryptoWallets.data.ethereum || []).forEach((wallet: any) => {
            if (wallet.balances) {
              // ETH nativa
              if (wallet.balances.eth) {
                processedWallets.push({
                  address: wallet.address,
                  balance: wallet.balances.eth,
                  currency: 'ETH',
                  displayName: 'Ethereum',
                  isToken: false,
                  network: 'ETHEREUM'
                });
              }
              // USDT ERC20
              if (wallet.balances.usdt_erc20) {
                processedWallets.push({
                  address: wallet.address,
                  balance: wallet.balances.usdt_erc20,
                  currency: 'USDT_ERC20',
                  displayName: 'Ethereum/USDT',
                  isToken: true,
                  network: 'ETHEREUM'
                });
              }
              // USDC ERC20
              if (wallet.balances.usdc_erc20) {
                processedWallets.push({
                  address: wallet.address,
                  balance: wallet.balances.usdc_erc20,
                  currency: 'USDC_ERC20',
                  displayName: 'Ethereum/USDC',
                  isToken: true,
                  network: 'ETHEREUM'
                });
              }
            }
          });

          // BNB Chain (BNB nativa + tokens BEP20)
          (cryptoWallets.data.bnb || []).forEach((wallet: any) => {
            if (wallet.balances) {
              // BNB nativa
              if (wallet.balances.bnb) {
                processedWallets.push({
                  address: wallet.address,
                  balance: wallet.balances.bnb,
                  currency: 'BNB',
                  displayName: 'BNB Chain',
                  isToken: false,
                  network: 'BNB'
                });
              }
              // USDT BEP20
              if (wallet.balances.usdt_bep20) {
                processedWallets.push({
                  address: wallet.address,
                  balance: wallet.balances.usdt_bep20,
                  currency: 'USDT_BEP20',
                  displayName: 'BNB Chain/USDT',
                  isToken: true,
                  network: 'BNB'
                });
              }
              // USDC BEP20
              if (wallet.balances.usdc_bep20) {
                processedWallets.push({
                  address: wallet.address,
                  balance: wallet.balances.usdc_bep20,
                  currency: 'USDC_BEP20',
                  displayName: 'BNB Chain/USDC',
                  isToken: true,
                  network: 'BNB'
                });
              }
            }
          });

          // Tron (TRX nativa + tokens TRC20)
          (cryptoWallets.data.tron || []).forEach((wallet: any) => {
            if (wallet.balances) {
              // TRX nativa
              if (wallet.balances.trx) {
                processedWallets.push({
                  address: wallet.address,
                  balance: wallet.balances.trx,
                  currency: 'TRX',
                  displayName: 'Tron',
                  isToken: false,
                  network: 'TRON'
                });
              }
              // USDT TRC20
              if (wallet.balances.usdt_trc20) {
                processedWallets.push({
                  address: wallet.address,
                  balance: wallet.balances.usdt_trc20,
                  currency: 'USDT_TRC20',
                  displayName: 'Tron/USDT',
                  isToken: true,
                  network: 'TRON'
                });
              }
            }
          });

          // Solana (SOL nativa + tokens SPL)
          (cryptoWallets.data.solana || []).forEach((wallet: any) => {
            if (wallet.balances) {
              // SOL nativa
              if (wallet.balances.sol) {
                processedWallets.push({
                  address: wallet.address,
                  balance: wallet.balances.sol,
                  currency: 'SOL',
                  displayName: 'Solana',
                  isToken: false,
                  network: 'SOLANA'
                });
              }
              // USDT SPL
              if (wallet.balances.usdt_spl) {
                processedWallets.push({
                  address: wallet.address,
                  balance: wallet.balances.usdt_spl,
                  currency: 'USDT_SPL',
                  displayName: 'Solana/USDT',
                  isToken: true,
                  network: 'SOLANA'
                });
              }
              // USDC SPL
              if (wallet.balances.usdc_spl) {
                processedWallets.push({
                  address: wallet.address,
                  balance: wallet.balances.usdc_spl,
                  currency: 'USDC_SPL',
                  displayName: 'Solana/USDC',
                  isToken: true,
                  network: 'SOLANA'
                });
              }
            }
          });

          console.log('🪙 Crypto wallets procesadas:', processedWallets);
          setWallets((prev) => ({
            ...prev,
            crypto: processedWallets,
          }));
        }
      } catch (error) {
        console.error('❌ Error fetching wallet data:', error);
      }
    };

    fetchData();
  }, [mainWallets, cryptoWallets, zenusAccount]);

  // Calcular costos cuando cambie el monto o la wallet (solo para crypto)
  useEffect(() => {
    if (option === 'crypto' && amount && wallet && wallets.crypto.length > 0) {
      const selectedWallet = wallets.crypto.find((w) => w.currency === wallet);
      if (selectedWallet) {
        calculateReloadCosts(amount, selectedWallet);
      }
    } else {
      setReloadCosts(null);
    }
  }, [amount, wallet, option, wallets.crypto, cardId]);

  // Calcular costos para Zenus (FIAT)
  useEffect(() => {
    if (option === 'main' && amount && wallet === 'USD') {
      calculateZenusTopupCosts(amount);
    } else if (option === 'main') {
      setZenusTopupCosts(null);
    }
  }, [amount, wallet, option]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
          {/* Fixed Header */}
          <div className="px-6 pt-6 pb-4 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-800">{t('cards.topUpModal.title')}</h2>
          </div>

          {/* Scrollable Content */}
          <div className="px-6 py-4 overflow-y-auto flex-1">
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label htmlFor="wallet" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('cards.topUpModal.selectOption')}
                </label>
                <select value={option} onChange={(e) => { setOption(e.target.value) }} className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value={"main"}>{t('cards.topUpModal.fiatWallet')}</option>
                  <option value={"crypto"}>{t('cards.topUpModal.cryptoWallet')}</option>
                </select>
              </div>
              <div className="mb-4">
                <label htmlFor="wallet" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('cards.topUpModal.selectWallet')}
                </label>
                <select
                  id="wallet"
                  value={wallet}
                  onChange={(e) => changeWallet(e.target.value)}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="" disabled>{t('cards.topUpModal.selectWalletPlaceholder')}</option>
                  {option === "crypto" && (
                    <>
                      {wallets.crypto.length > 0 ? (
                        wallets.crypto.map((w, index) => (
                          <option key={`${w.currency}-${w.address}-${index}`} value={w.currency}>
                            {w.displayName} : {truncateDecimals(Number(w.balance || 0), 8)} {w.currency.includes('_') ? w.currency.split('_')[0] : w.currency}
                          </option>
                        ))
                      ) : (
                        <option disabled>No crypto wallets available</option>
                      )}
                    </>
                  )}
                  {option === "main" && (
                    <>
                      {wallets?.fiat?.length > 0 && (
                        <optgroup label={t('cards.topUpModal.walletGroups.fiatWallets')}>
                          {wallets?.fiat?.map((w) => (
                            <option key={w.currency} value={w.currency}>
                              {FIAT_WALLETS.find((item) => item.currency === w.currency)?.name || w.currency} : {Number(w.balance || 0).toFixed(2)} {(FIAT_WALLETS.find((item) => item.currency === w.currency) as any)?.shortName || w.currency}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </>
                  )}
                </select>
              </div>
              <div className="mb-4">
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('cards.topUpModal.topUpAmountEUR')}
                </label>
                <input
                  id="amount"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value === "" ? "" : e.target.value)}
                  onBlur={() => setAmount(amount === "" ? "" : amount)}
                  min="10"
                  max={option === 'crypto' ? "10000" : "5000"}
                  step="0.01"
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('cards.topUpModal.enterAmount')}
                  required
                />
                {option === 'crypto' && wallet && (
                  <div className="mt-2 text-sm">
                    {isCalculating ? (
                      <p className="text-blue-500">{t('cards.topUpModal.processing')}</p>
                    ) : reloadCosts ? (
                      <div className="bg-blue-50 border border-blue-200 rounded-md p-3 space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-700">Amount to card:</span>
                          <span className="font-medium">€{reloadCosts.amountEUR.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Commission ({(reloadCosts.commissionRate * 100).toFixed(0)}%):</span>
                          <span className="font-medium">€{reloadCosts.commissionEUR.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between border-t border-blue-300 pt-1 mt-1">
                          <span className="font-bold text-gray-800">Total to pay:</span>
                          <span className="font-bold text-[#B3A369]">€{reloadCosts.netAmountEUR.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-xs text-gray-600 pt-1">
                          <span>You will transfer:</span>
                          <span className="font-medium">{reloadCosts.totalCrypto.toFixed(6)} {reloadCosts.cryptoSymbol}</span>
                        </div>
                        {(() => {
                          const selectedWallet = wallets.crypto.find((w) => w.currency === wallet);
                          const walletBalance = selectedWallet ? parseFloat(selectedWallet.balance) : 0;
                          const hasEnough = walletBalance >= reloadCosts.totalCrypto;
                          return (
                            <div className={`text-xs pt-1 ${hasEnough ? 'text-green-600' : 'text-red-600'}`}>
                              {hasEnough ? '✅' : '❌'} Balance: {walletBalance.toFixed(6)} {reloadCosts.cryptoSymbol}
                            </div>
                          );
                        })()}
                      </div>
                    ) : null}
                  </div>
                )}
                {option === 'main' && wallet === 'USD' && (
                  <div className="mt-2 text-sm">
                    {isCalculating ? (
                      <p className="text-blue-500">{t('cards.topUpModal.processing')}</p>
                    ) : zenusTopupCosts && zenusTopupCosts.amountEUR !== undefined ? (
                      <div className="bg-blue-50 border border-blue-200 rounded-md p-3 space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-700">Amount to card:</span>
                          <span className="font-medium">€{(zenusTopupCosts.amountEUR || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Commission (2%):</span>
                          <span className="font-medium">€{(zenusTopupCosts.bankCommissionEUR || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between border-t border-blue-300 pt-1 mt-1">
                          <span className="font-bold text-gray-800">Total to pay:</span>
                          <span className="font-bold text-[#B3A369]">€{(zenusTopupCosts.totalEUR || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-xs text-gray-600 pt-1">
                          <span>You will transfer:</span>
                          <span className="font-medium">${(zenusTopupCosts.totalUSD || 0).toFixed(2)} USD</span>
                        </div>
                        {(() => {
                          const selectedWallet = wallets.fiat.find((w) => w.currency === 'USD');
                          const walletBalance = selectedWallet ? parseFloat(selectedWallet.balance) : 0;
                          const hasEnough = walletBalance >= (zenusTopupCosts.totalUSD || 0);
                          return (
                            <div className={`text-xs pt-1 ${hasEnough ? 'text-green-600' : 'text-red-600'}`}>
                              {hasEnough ? '✅' : '❌'} Balance: ${walletBalance.toFixed(2)} USD
                            </div>
                          );
                        })()}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
              <div className="p-3 bg-yellow-200 rounded-md mb-3">
                <h2 className="text-lg font-semibold mb-2">{t('cards.topUpModal.importantTitle')}</h2>
                {option === "crypto" && (
                  <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                    <li>{t('cards.topUpModal.cryptoWarnings.warning1')}</li>
                    <li>{t('cards.topUpModal.cryptoWarnings.warning2')}</li>
                    <li>{t('cards.topUpModal.cryptoWarnings.warning3')}</li>
                    <li>{t('cards.topUpModal.cryptoWarnings.warning4')}</li>
                    <li>{t('cards.topUpModal.cryptoWarnings.warning5')}</li>
                  </ul>
                )}
                {option === "main" && (
                  <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                    <li>{t('cards.topUpModal.fiatWarnings.warning1')}</li>
                    <li>{t('cards.topUpModal.fiatWarnings.warning2')}</li>
                  </ul>
                )}
              </div>
            </form>
          </div>

          {/* Fixed Footer with Action Buttons */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => onClose(false)}
                className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300"
                disabled={isPending ? true : false}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isPending}
                onClick={handleSubmit}
                className="px-4 py-2 text-white bg-belobaba-gold rounded-md hover:bg-[#a39a22] disabled:bg-gray-400"
              >
                {isPending ? t('cards.topUpModal.processing') : t('cards.topUpModal.topUp')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
